/**
 * Sound library for the Discord Activity (#3972).
 *
 * Each cue is synthesized at play-time via the Web Audio API rather than
 * shipped as an asset. The five gentle event chimes plus the optional button
 * cue cost less than 200 lines of code and zero bytes of network traffic, and
 * the synthesis path keeps us out of any sample-licensing conversation.
 *
 * Tone targets — kept deliberately understated:
 *
 *  - `testAvailable` rises (C5 → E5)  → "something arrived"
 *  - `testCompleted` falls (E5 → C5)  → "something finished"
 *  - `testClaimed`   single G5 pluck   → "an action was taken"
 *  - `testerOnline`  high A5 pip       → "presence in"
 *  - `testerOffline` low E4 pip        → "presence out"
 *  - `buttonPress`   very quiet A4     → reserved for future button feedback
 *
 * Every cue resolves in ≤ 300ms (issue AC). `peak` values stack with the
 * engine's master gain (~0.3) — picking peaks in the 0.3–0.6 range keeps the
 * effective amplitude well below 0.2 so the chimes sit under the OS volume
 * curve instead of over it.
 */

export type SoundName =
  | 'testAvailable'
  | 'testClaimed'
  | 'testCompleted'
  | 'testerOnline'
  | 'testerOffline'
  | 'buttonPress';

export interface SoundNote {
  readonly freq: number;
  readonly type: OscillatorType;
  /** Seconds from the start of the cue. */
  readonly delay: number;
  /** Linear ramp from 0 → peak (seconds). */
  readonly attack: number;
  /** Hold at peak (seconds). */
  readonly sustain: number;
  /** Exponential ramp from peak → near-zero (seconds). */
  readonly release: number;
  /** Per-note gain in 0..1, multiplied by the engine's master gain. */
  readonly peak: number;
}

export interface SoundSpec {
  readonly notes: readonly SoundNote[];
}

const C5 = 523.25;
const E5 = 659.25;
const G5 = 783.99;
const A5 = 880.0;
const A4 = 440.0;
const E4 = 329.63;

export const SOUND_LIBRARY: Readonly<Record<SoundName, SoundSpec>> = {
  testAvailable: {
    notes: [
      { freq: C5, type: 'sine', delay: 0.0, attack: 0.02, sustain: 0.05, release: 0.1, peak: 0.55 },
      { freq: E5, type: 'sine', delay: 0.1, attack: 0.02, sustain: 0.05, release: 0.12, peak: 0.5 },
    ],
  },
  testClaimed: {
    notes: [
      { freq: G5, type: 'triangle', delay: 0.0, attack: 0.01, sustain: 0.04, release: 0.14, peak: 0.5 },
    ],
  },
  testCompleted: {
    notes: [
      { freq: E5, type: 'sine', delay: 0.0, attack: 0.02, sustain: 0.05, release: 0.1, peak: 0.5 },
      { freq: C5, type: 'sine', delay: 0.1, attack: 0.02, sustain: 0.06, release: 0.12, peak: 0.6 },
    ],
  },
  testerOnline: {
    notes: [
      { freq: A5, type: 'sine', delay: 0.0, attack: 0.01, sustain: 0.02, release: 0.1, peak: 0.35 },
    ],
  },
  testerOffline: {
    notes: [
      { freq: E4, type: 'sine', delay: 0.0, attack: 0.01, sustain: 0.02, release: 0.11, peak: 0.35 },
    ],
  },
  buttonPress: {
    notes: [
      { freq: A4, type: 'triangle', delay: 0.0, attack: 0.005, sustain: 0.015, release: 0.06, peak: 0.25 },
    ],
  },
};
